import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { GoalModal } from '../components/GoalModal';
import { goalSubSectorsApi, goalsApi } from '../budgetGoalsApi';
import type { GoalListItem } from '../budgetGoalTypes';

/**
 * The goals themselves — what a budget plan selects into its Goal Setting and a PPA aligns to.
 *
 * Reached through **Procurement → Investment Planning → Settings**.
 */
export function GoalsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<GoalListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    goal: GoalListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // A goal cannot be created before a sub-sector exists. Seeded MET; a failed feed leaves it
  // met rather than accusing a working module of being unset up.
  const [hasSubSectors, setHasSubSectors] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    goalSubSectorsApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasSubSectors(rows.length > 0);
      })
      .catch(() => {
        // Leaves it met.
      });

    return () => controller.abort();
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchGoals = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      goalsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          sector: query.facets.sector?.join(','),
          subSector: query.facets.subSector?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (goal: GoalListItem): MenuItem[] => [
      {
        key: `edit-${goal.id}`,
        label: 'Edit',
        permission: 'budget-goals.update',
        onClick: () => setEditing(goal),
      },
      {
        key: `restore-${goal.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'budget-goals.delete',
        disabledReason: goal.isActive ? 'This goal is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', goal }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await goalsApi.deactivate(confirming.goal.id);
      else await goalsApi.activate(confirming.goal.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<GoalListItem>
        module="budget-goals"
        moduleNoun="goals"
        breadcrumb={[
          'Procurement',
          'Investment Planning',
          { label: 'Settings', to: '/procurement/investment-planning/settings' },
          'Goals',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'sector', label: 'Sector' },
          { key: 'subSector', label: 'Sub-sector' },
        ]}
        folderNavFacet="sector"
        auditEntityTypes={['Goal']}
        rowKey={(goal) => goal.id}
        fetch={fetchGoals}
        reloadKey={reloadKey}
        searchPlaceholder="Search goals, objectives or strategies"
        emptyMessage="No goals yet. Add the ones this organization's investment programme pursues."
        prerequisites={[
          {
            label: 'a sub-sector',
            met: hasSubSectors,
            to: '/procurement/investment-planning/settings/goal-sub-sectors',
            permission: 'budget-goals.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="budget-goals.create"
        onRowOpen={(goal) => navigate(`/procurement/investment-planning/settings/goals/${goal.id}`)}
        openPermission="budget-goals.read"
        rowActions={rowActions}
        onDelete={(goal) => setConfirming({ kind: 'deactivate', goal })}
        deletePermission="budget-goals.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(goal) => (goal.isActive ? null : 'This goal is already deleted.')}
        onClearData={() => goalsApi.clear()}
        clearDataPermission="budget-goals.clear-data"
        otherInfo={(goal) => goal}
      />

      {creating || editing !== null ? (
        <GoalModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete goal' : 'Restore goal'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`goal-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.goal.name}" stops being offered on a budget plan's Goal Setting and on a PPA's alignment.`
                  : `"${confirming.goal.name}" is offered again on a budget plan's Goal Setting and on a PPA's alignment.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  Plans and PPAs that already name it keep resolving it — deleting a word does
                  not rewrite what was recorded.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted goal can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<GoalListItem>[] = [
  {
    key: 'name',
    header: 'Goal',
    width: 280,
    flex: true,
    sortable: true,
    render: (goal) => <TextCell value={goal.name} />,
  },
  {
    key: 'sectorName',
    header: 'Sector',
    width: 160,
    sortable: true,
    summary: true,
    render: (goal) => <TextCell value={goal.sectorName} />,
  },
  {
    key: 'subSectorName',
    header: 'Sub-sector',
    width: 180,
    sortable: true,
    summary: true,
    render: (goal) => <TextCell value={goal.subSectorName} />,
  },
  {
    key: 'objective',
    header: 'Objective',
    width: 320,
    summary: true,
    render: (goal) => <TextCell value={goal.objective} />,
  },
  {
    key: 'strategy',
    header: 'Strategy',
    width: 320,
    render: (goal) => <TextCell value={goal.strategy} />,
  },
  {
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (goal) => <TextCell value={String(goal.sortOrder)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (goal) =>
      goal.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
